import * as React from "react"
import { router } from "@inertiajs/react"
import { Search } from "lucide-react"
import { user_roles_path } from "@/lib/routes"
import {
  Dialog, DialogTrigger, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogClose,
} from "@/components/ui/Dialog"
import { Button } from "@/components/ui/Button"
import { Input } from "@/components/ui/Input"
import RadioCircle from "@/components/ui/RadioCircle"
import WorkplaceListbox from "@/components/WorkplaceListbox"
import { deriveWorkplaces } from "@/lib/orgTree"
import { cn } from "@/lib/utils"
import type { Role, Member, OrgTreeNode } from "../types"

type Props = {
  role: Role
  members: Member[]
  orgTree: OrgTreeNode[]
  trigger: React.ReactNode
}

// Grant this role to a member at an org node. Same "Where" picker as the
// Members page Assign-role dialog; the role is fixed, so the picker is "Who".
export default function AddPersonDialog({ role, members, orgTree, trigger }: Props) {
  const [open, setOpen] = React.useState(false)
  const [userId, setUserId] = React.useState<string | null>(null)
  const [nodeId, setNodeId] = React.useState<string | null>(null)
  const [query, setQuery] = React.useState("")
  const [submitting, setSubmitting] = React.useState(false)

  const workplaces = React.useMemo(() => deriveWorkplaces(orgTree), [orgTree])

  // (user, node) pairs already assigned — the same pair cannot be added twice.
  const taken = React.useMemo(() => {
    const set = new Set<string>()
    for (const p of role.people) for (const n of p.nodes) set.add(`${p.user_id}::${n.org_node_id}`)
    return set
  }, [role.people])
  const duplicate = userId !== null && nodeId !== null && taken.has(`${userId}::${nodeId}`)

  const visible = React.useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return members
    return members.filter((m) => m.name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q))
  }, [members, query])

  const reset = () => {
    setUserId(null)
    setNodeId(null)
    setQuery("")
  }

  const submit = () => {
    if (!userId || !nodeId || duplicate) return
    setSubmitting(true)
    router.post(
      user_roles_path(),
      { user_role: { user_id: userId, role_id: role.id, org_node_id: nodeId } },
      {
        preserveState: true,
        preserveScroll: true,
        onSuccess: () => { setOpen(false); reset() },
        onFinish: () => setSubmitting(false),
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { setOpen(next); if (!next) reset() }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add person to {role.name}</DialogTitle>
        </DialogHeader>

        <div className="-mx-1 flex max-h-[60vh] flex-col gap-6 overflow-y-auto px-1">
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-foreground">Who</h3>
            <div className="relative">
              <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Filter by name or email"
                className="pl-9"
                aria-label="Filter members"
              />
            </div>
            <div className="max-h-56 overflow-y-auto rounded-md border border-border" role="listbox" aria-label="Members">
              {visible.length === 0 ? (
                <p className="px-4 py-6 text-center text-sm text-muted-foreground">No match.</p>
              ) : (
                visible.map((m) => {
                  const active = m.id === userId
                  return (
                    <button
                      key={m.id}
                      type="button"
                      role="option"
                      aria-selected={active}
                      onClick={() => setUserId(m.id)}
                      className={cn(
                        "flex w-full items-center gap-3 px-3 py-2 text-left transition-colors",
                        active ? "bg-primary/5" : "hover:bg-muted/50"
                      )}
                    >
                      <RadioCircle active={active} />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-foreground">{m.name}</span>
                        <span className="block truncate text-xs text-muted-foreground">{m.email}</span>
                      </span>
                    </button>
                  )
                })
              )}
            </div>
          </div>

          <div className="space-y-2">
            <h3 className="text-sm font-medium text-foreground">Where</h3>
            <WorkplaceListbox workplaces={workplaces} selectedId={nodeId} onSelect={setNodeId} />
            <p className="text-xs text-muted-foreground">
              {duplicate
                ? "Already holds this role there."
                : "Permissions cascade down from the selected node."}
            </p>
          </div>
        </div>

        <DialogFooter>
          <DialogClose asChild><Button type="button" variant="ghost">Cancel</Button></DialogClose>
          <Button type="button" disabled={!userId || !nodeId || duplicate || submitting} onClick={submit}>
            Add
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
